// The two things a punter sets against themselves: a limit, and a break.
//
// Nothing in this file reads the store, the clock or the DOM — a profile and a
// moment in, an answer out — which is why every rule below can be pinned in the
// node suite rather than argued about on a screen.
//
// THE THREE RULES, and each of them is the reason the file exists:
//
//   A LOWER LIMIT APPLIES NOW. Somebody who has just decided $20 a bet is
//   enough must not have to wait a day for the app to agree with them.
//
//   A HIGHER LIMIT, OR NONE AT ALL, APPLIES IN 24 HOURS. A limit that can be
//   lifted in the moment it starts to bite is not a limit, it is a speed bump.
//   The new pair is parked on the profile as `pendingLimits` and promoted on
//   read, so nothing has to be running at the moment it falls due.
//
//   A BREAK CANNOT BE ENDED EARLY. There is no function here that shortens one,
//   and `startBreak` only ever moves the date further out. The way back is the
//   day arriving.
//
// DAYS ARE SYDNEY DAYS. "$45 of your $50 today" and "on a break until Friday"
// are both claims about a calendar day, and a calendar day on a phone in Perth
// is not the one the board, the settle job and the closing prices all use.
// `breakUntil` is stored as a bare 'YYYY-MM-DD' — the first day you are NOT on
// a break — so every comparison here is a string comparison and no offset
// arithmetic can get it wrong either side of a daylight-saving change.

// `localDate` is tickets.js's, the same one the paper trail counts its days
// with. tickets.js imports nothing itself, so this stays a cheap module.
import { localDate } from './tickets.js';
// The no-value glyph, from the one place it is typed.
import { NO_VALUE } from './format.js';

/** The zone the board, the settle and the record all keep their days in. */
export const SYDNEY = 'Australia/Sydney';

/** How long a raised or lifted limit waits before it means anything. */
export const RAISE_DELAY_MS = 24 * 60 * 60 * 1000;

/** The four lengths Settings offers, in days. */
export const BREAK_LENGTHS = [
  { value: '1', label: '1 day', days: 1 },
  { value: '7', label: '1 week', days: 7 },
  { value: '30', label: '1 month', days: 30 },
  { value: '90', label: '3 months', days: 90 },
];

/** Cents, never floating dust — the same rounding the rest of the app uses. */
const cents = (n) => Math.round(n * 100) / 100;

/** A positive amount, or null. Blank, zero and rubbish are all "no limit". */
export function parseLimit(raw) {
  if (raw === null || raw === undefined) return null;
  const text = String(raw).replace(/[$,\s]/g, '');
  const n = Number(text);
  return text && Number.isFinite(n) && n > 0 ? cents(n) : null;
}

/**
 * "$20", "$12.50" — money in the middle of a sentence.
 *
 * Not `format.js`'s `money`, on purpose. That one pads every figure to the cent
 * because it prints in columns of figures that have to line up; this prints
 * inside "Your limit is $20 a bet", where "$20.00" reads as an amount somebody
 * was quoted rather than the round number they typed in themselves.
 */
export function dollars(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return NO_VALUE;
  return Number.isInteger(v) ? `$${v.toLocaleString('en-AU')}` : `$${v.toFixed(2)}`;
}

/**
 * May this stake go on?
 *
 * `{ ok }` and, when it is not, the sentence to print — one place, because the
 * Place alert on Today, the ticket screen's own field and `markPlaced` all ask
 * and all have to say the same thing.
 *
 * The daily test is the stake ON TOP of what is already down: a $50 day with
 * $45 on it has $5 left, not another $50. Where nothing has gone on yet there is
 * no "$0 of your $50" to say, so the sentence is the limit itself.
 */
export function stakeAllowed({
  stake, todayStaked = 0, maxStake = null, maxDaily = null, bookCap: cap = null, bookmakerName = '',
} = {}) {
  const amount = Number(stake);
  if (!Number.isFinite(amount) || amount <= 0) return { ok: true, reason: null, text: '' };

  if (Number.isFinite(maxStake) && maxStake > 0 && amount > maxStake) {
    return { ok: false, reason: 'stake', text: `Your limit is ${dollars(maxStake)} a bet.` };
  }

  // THE BOOK'S CAP, AFTER THE PUNTER'S OWN. A limit somebody set against
  // themselves is the one that should bite first: being told "your book will
  // not take this" about a bet you had already decided not to make is the app
  // answering the wrong question.
  if (Number.isFinite(cap) && cap > 0 && amount > cap) {
    const who = bookmakerName ? `${bookmakerName} takes` : 'This book takes';
    return { ok: false, reason: 'book', text: `${who} ${dollars(cap)} a bet from you.` };
  }

  const already = Number.isFinite(Number(todayStaked)) ? Number(todayStaked) : 0;
  if (Number.isFinite(maxDaily) && maxDaily > 0 && cents(already + amount) > maxDaily) {
    return {
      ok: false,
      reason: 'daily',
      text: already > 0
        ? `${dollars(already)} of your ${dollars(maxDaily)} today is already placed.`
        : `Your limit is ${dollars(maxDaily)} a day.`,
    };
  }

  return { ok: true, reason: null, text: '' };
}

// --- the OTHER limit: the one the bookmaker sets ---------------------------------
//
// THE BINDING CONSTRAINT IS NOT MODEL QUALITY, IT IS ACCOUNT LIMITATION.
// Kaunitz's team were limited within months while winning, and every punter who
// only ever takes a price that is out of line gets there. Two facts follow, and
// both of them belong on a screen rather than in a note somewhere:
//
//   RACING HAS MINIMUM BET LAWS AND SPORT DOES NOT. A licensed bookmaker in
//   NSW, VIC, QLD and SA has to lay a bet to lose a set amount on a metro
//   thoroughbred win market, on the phone or on its site, from an hour out. The
//   figures below are those schedules. They are not a promise — a book may
//   still close an account outright, and the rules cover the WIN and PLACE
//   markets of a licensed meeting and nothing else — but they are the floor a
//   racing bet can be sized against.
//
//   A SPORT BET HAS NO SUCH FLOOR AT ALL. Whatever a book will take is whatever
//   it feels like taking that day, and the honest default is no number.
//
// So a cap is per BOOK and per CODE, it defaults to the schedule, and a punter
// who has been cut back types what they are actually allowed. Nothing here
// stops anybody betting: it stops the app proposing a stake the punter's own
// book will not accept, which is worse than useless — it is a ticket that
// cannot be placed, printed as though it could.

/**
 * The minimum-bet schedules, by racing code, as the default cap per book.
 *
 * Metropolitan thoroughbred meetings carry the biggest number and country
 * meetings a smaller one; harness and greyhounds carry their own. Place markets
 * are covered at a lower figure, and the app only ever proposes a WIN bet — the
 * place figure is here because Settings prints the pair, and because a punter
 * reading the caps deserves the schedule they are actually protected by.
 */
export const MBL = Object.freeze({
  'thoroughbred-metro': { win: 2000, place: 800 },
  'thoroughbred-country': { win: 1000, place: 400 },
  harness: { win: 1000, place: 400 },
  greyhound: { win: 1000, place: 400 },
  // Not a schedule: the absence of one. Sport has no minimum-bet law in any
  // Australian state, so there is no default to print and no floor to size to.
  sport: { win: null, place: null },
});

/** The one sentence Settings prints over the caps. */
export const CAPS_NOTE = 'Books limit winning accounts. Racing has minimum bet laws; sport does not.';

/**
 * What a book will take on this bet before the punter has said otherwise.
 *
 * `code` is one of `MBL`'s keys — `codeFor` in lib/racing/trust.js answers it
 * for a race, and everything that is not a race is 'sport'.
 */
export function defaultCap(code, market = 'win') {
  const schedule = MBL[code] ?? MBL.sport;
  return schedule[market] ?? null;
}

/**
 * The cap that actually applies at one book: what the punter typed, or the
 * schedule, or nothing at all.
 *
 * `books` is `profile.bookLimits` — `{ [bookmakerKey]: { maxBet, limited,
 * limitedAt } }`. A punter's own number always wins, in both directions: a book
 * that has cut them to $50 on the gallops is $50 whatever the schedule says,
 * and one that has been taking $5,000 is $5,000.
 */
export function bookCap(books, bookmakerKey, { code = 'sport', market = 'win' } = {}) {
  const own = parseLimit(books?.[bookmakerKey]?.maxBet);
  return own ?? defaultCap(code, market);
}

/** Has this book limited the punter, and when did they say so? */
export function bookLimited(books, bookmakerKey) {
  const row = books?.[bookmakerKey];
  return {
    limited: Boolean(row?.limited),
    since: typeof row?.limitedAt === 'string' && row.limitedAt.length === 10 ? row.limitedAt : null,
  };
}

/**
 * "Neds has limited you since Fri 12 Sep." — or the same without the date, or
 * nothing at all.
 *
 * The punter's own record of their own account, said back to them plainly. It
 * is not a warning and it is not a judgement: a limited account is what
 * happens to somebody who bets well, and the app's job is to size around it.
 */
export function limitedLine(books, bookmakerKey, bookmakerName) {
  const { limited, since } = bookLimited(books, bookmakerKey);
  if (!limited) return '';
  const who = bookmakerName || bookmakerKey;
  return since ? `${who} has limited you since ${dateLabel(since)}.` : `${who} has limited you.`;
}

/** The record after marking a book limited or not, and with what cap. */
export function setBookLimit(books, bookmakerKey, { maxBet, limited, today = null } = {}) {
  const key = String(bookmakerKey ?? '');
  if (!key) return { ...(books ?? {}) };
  const current = books?.[key] ?? {};
  const next = { ...current };

  if (maxBet !== undefined) {
    const parsed = parseLimit(maxBet);
    if (parsed === null) delete next.maxBet;
    else next.maxBet = parsed;
  }
  if (limited !== undefined) {
    if (limited) {
      next.limited = true;
      // The date is stamped when the flag goes ON and kept if it already is:
      // "limited since" is about when it happened, and re-saving Settings is
      // not a second limitation.
      next.limitedAt = current.limited && current.limitedAt ? current.limitedAt : today;
    } else {
      delete next.limited;
      delete next.limitedAt;
    }
  }

  const out = { ...(books ?? {}) };
  if (Object.keys(next).length) out[key] = next;
  else delete out[key];
  return out;
}

/** Is `next` tighter than `current` — a new limit, or a smaller one? */
const tightens = (current, next) =>
  Number.isFinite(next) && next > 0 && (!Number.isFinite(current) || current <= 0 || next < current);

/**
 * A change to the limits, as the patch to save.
 *
 * Every field that TIGHTENS takes effect in this call. Anything that loosens —
 * a bigger number, or a box emptied — is parked whole in `pendingLimits` with
 * the moment it falls due, and `liveLimits` promotes it from there.
 *
 * The pending pair is the WHOLE next pair, not just the loosened half, so
 * promoting it can never walk back a limit that was tightened in the same edit:
 * that half of the pair already holds the tighter number. It also replaces any
 * pending change outright — the two boxes on the screen are what the punter
 * means now, and a raise they have since thought better of should not arrive
 * tomorrow out of a queue.
 */
export function applyLimitChange(current = {}, next = {}, now = Date.now()) {
  const from = { maxStake: current.maxStake ?? null, maxDaily: current.maxDaily ?? null };
  const to = { maxStake: parseLimit(next.maxStake), maxDaily: parseLimit(next.maxDaily) };

  const patch = { maxStake: from.maxStake, maxDaily: from.maxDaily, pendingLimits: null };
  let waiting = false;

  for (const field of ['maxStake', 'maxDaily']) {
    if (to[field] === from[field]) continue;
    if (tightens(from[field], to[field])) patch[field] = to[field];
    else waiting = true;
  }

  if (waiting) patch.pendingLimits = { ...to, at: now + RAISE_DELAY_MS };
  return patch;
}

/**
 * The limits AS THEY STAND — the profile's own pair, with a pending change
 * folded in once its 24 hours are up.
 *
 * Promoted on read rather than written back by a timer: a phone that was off
 * all night still comes back to the limits it was promised, and nothing has to
 * be running at 3am to keep that true.
 */
export function liveLimits(profile = {}, now = Date.now()) {
  const pending = profile?.pendingLimits;
  const due = pending && Number.isFinite(Number(pending.at)) && Number(pending.at) <= now;
  return due
    ? { maxStake: pending.maxStake ?? null, maxDaily: pending.maxDaily ?? null }
    : { maxStake: profile?.maxStake ?? null, maxDaily: profile?.maxDaily ?? null };
}

/** A pending change still waiting, or null. What Settings prints under the boxes. */
export function pendingLimitsOf(profile = {}, now = Date.now()) {
  const pending = profile?.pendingLimits;
  if (!pending || !Number.isFinite(Number(pending.at))) return null;
  return Number(pending.at) > now ? pending : null;
}

/** Are we on a break? True until the Sydney day `breakUntil` names arrives. */
export function onBreak(profile = {}, now = Date.now()) {
  const until = profile?.breakUntil;
  return typeof until === 'string' && until.length === 10 && localDate(now, SYDNEY) < until;
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * "Fri 20 Sep" — a bare calendar day, written out.
 *
 * Not `format.js`'s `dayShort`, and the reason is worth the three lines it
 * costs: en-AU's short date is "Sun, 20 Sept" in a current ICU and "Sun 20 Sep"
 * in an older one, so the same sentence would read differently on two phones
 * and differently again in the test runner. A date with no time on it is a
 * calendar fact, and these are the words the app agreed to say about it.
 */
export function dateLabel(date) {
  if (typeof date !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(date)) return '';
  const [y, m, d] = date.split('-').map(Number);
  // UTC throughout: the parts go in as themselves and the weekday comes back
  // from the same calendar, whatever zone the device is in.
  const at = new Date(Date.UTC(y, m - 1, d));
  return `${WEEKDAYS[at.getUTCDay()]} ${d} ${MONTHS[m - 1]}`;
}

/** "Fri 20 Sep" — the day you are back, or '' where there is no break. */
export const breakLabel = (profile = {}) => dateLabel(profile?.breakUntil);

/**
 * "On a break until Fri 20 Sep." — the sentence itself, in one place.
 *
 * Today prints it on the sheet that replaces the feed, the ticket screen where
 * Place bet used to be, and Settings where the break was set. Three copies of a
 * sentence is three sentences a month from now.
 */
export function breakLine(profile = {}) {
  const label = breakLabel(profile);
  return label ? `On a break until ${label}.` : '';
}

/**
 * The day a break of `days` ends: the Sydney date `days` after today.
 *
 * `current` is whatever break is already on, and the answer is never earlier
 * than that — taking "1 day" during a three-month break is the one way an early
 * end could be smuggled in, and this is where it is refused.
 */
export function breakUntilDate(today, days, current = null) {
  const n = Math.max(1, Math.round(Number(days) || 0));
  const [y, m, d] = String(today).split('-').map(Number);
  const end = new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
  return typeof current === 'string' && current > end ? current : end;
}

/** What `takeBreak` saves: the date, and the notifications that go quiet with it. */
export function startBreak(profile = {}, days, now = Date.now()) {
  return {
    breakUntil: breakUntilDate(localDate(now, SYDNEY), days, profile?.breakUntil ?? null),
    // A break with the daily reminder still firing is the app asking somebody
    // back every morning of it.
    notifyDaily: false,
    notifyOutliers: false,
  };
}

/**
 * What today's bets have cost so far, on the Sydney day.
 *
 * Read off the bet records themselves rather than kept as a running total: a
 * total is a second copy of the truth, and the one place it could drift is the
 * one place it matters.
 */
export function stakedOn(records, date, tz = SYDNEY) {
  const rows = Array.isArray(records) ? records : [];
  let total = 0;
  for (const r of rows) {
    const at = Date.parse(r?.placedAt ?? '');
    if (!Number.isFinite(at) || localDate(at, tz) !== date) continue;
    const stake = Number(r?.stake);
    if (Number.isFinite(stake)) total += stake;
  }
  return cents(total);
}
